/**
 * @module app.cm
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MemberService } from 'app/cm/services/member-service';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class MemberMedications extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = false;
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected memberId: string | null = null;

    public medications: ReadonlyArray<CM.MemberMedication> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly context: CasePageContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ***********************************************////
    public async reload(): Promise<void> {
        if(!this.caseId) return;

        if(!this.memberId && this.context.case.id === this.caseId && this.context.case.member?.id) {
            this.memberId = this.context.case.member.id;
        } else if(!this.memberId) {
            await this.context.loadCase(this.caseId, ['member'], ['member.id']);
            this.memberId = this.context.case.member?.id || null;
        }

        await this.loadMemberDiagnoses();
    }

    protected applySort(event: SortChangeEvent): void {
        if(!this.memberId) { return; }
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.loadMemberDiagnoses();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberDiagnoses(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'drug',
            'drug.ActiveIngredients',
            'drug.category',
            'drug.therapeuticClasses',
            'frequency',
            'metadata.created.byUser',
            'mode',
        ];

        const fields: string[] = [
            'dateRange.*',
            'dosage',
            'drug.description',
            'frequency.description',
            'drug.ActiveIngredients.description',
            'drug.category.description',
            'drug.therapeuticClasses.description',
            'mode.description',
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadMedicationsByMember(this.memberId, options, includes, fields);
            this.medications = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
